import React, { useRef, useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import Icon from './Icon';
import MultiSelectFilterList from './MultiSelectFilterList';
import { IsimpleOption } from './multiSelectFilterUtil';

interface IMultiSelectFilterDropdownProps {
    values: IsimpleOption[];
    options: IsimpleOption[];
    onApply: (selected: IsimpleOption[]) => void;
    placeholder: string;
    labelPrefix?: string; // e.g. "Room: " — rendered as the greyed prefix on the toggle
    searchPlaceholder?: string;
    emptyLabel?: string;
    disabled?: boolean;
}

// The toolbar is not translated (siblings hardcode "Room: All" etc.), so keep labels inline.
const CLEAR_LABEL = 'Clear Selection';
const APPLY_LABEL = 'Apply';

const MultiSelectFilterDropdown: React.FC<IMultiSelectFilterDropdownProps> = ({
    values,
    options,
    onApply,
    placeholder,
    labelPrefix,
    searchPlaceholder = 'Search...',
    emptyLabel = 'No options',
    disabled = false
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [positionClass, setPositionClass] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState<IsimpleOption[]>([]);
    // Bumped on each open to remount the list and reset its internal search term.
    const [openKey, setOpenKey] = useState(0);

    const count = values.length;
    const isDefaultValue = count === 0;

    // Build the greyed "label:" prefix from labelPrefix or the placeholder ("Room: All").
    let labelPart: string | null = null;
    if (labelPrefix) {
        labelPart = labelPrefix.replace(':', '').trim();
    } else {
        const parts = placeholder.split(': ');
        labelPart = parts.length > 1 && parts[0] ? parts[0] : null;
    }

    let valuePart: string;
    if (count === 0) {
        const parts = placeholder.split(': ');
        valuePart = parts.length > 1 ? parts.slice(1).join(': ') : placeholder;
    } else if (count === 1) {
        valuePart = values[0]?.label ?? '';
    } else {
        valuePart = `${count} selected`;
    }

    const calculatePosition = () => {
        if (!wrapperRef.current) return;
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const dropdownWidth = 260;
        const wouldOverflowRight = wrapperRect.left + dropdownWidth > screenWidth - 20;
        const wouldOverflowLeft = wrapperRect.right - dropdownWidth < 20;
        if (wouldOverflowRight && wouldOverflowLeft) {
            setPositionClass('dropdown-center');
        } else if (wouldOverflowRight) {
            setPositionClass('dropdown-right');
        } else {
            setPositionClass('');
        }
    };

    // Re-initialise the draft from the committed values every time the menu opens.
    // Closing without pressing Apply therefore discards the in-progress edits.
    const handleToggle = (nextOpen: boolean) => {
        setIsOpen(nextOpen);
        if (nextOpen) {
            setDraft(values.map(v => ({ value: v.value, label: v.label })));
            setOpenKey(prev => prev + 1);
            setTimeout(() => calculatePosition(), 0);
        }
    };

    const handleApply = () => {
        onApply(draft);
        setIsOpen(false);
    };

    const handleClearDraft = () => {
        setDraft([]);
    };

    // The toggle "✕" commits an immediate clear-all (matches single-select FilterDropdown).
    const handleClearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onApply([]);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleResize = () => calculatePosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div ref={wrapperRef} className={`filter-dropdown-wrapper multi-select-filter ${disabled ? 'disabled' : ''}`}>
            <Dropdown
                show={isOpen}
                onToggle={handleToggle}
                className="filter-dropdown"
                id="multi-select-filter-dropdown"
            >
                <Dropdown.Toggle disabled={disabled} className="filter-dropdown-toggle" as="button">
                    <span className="filter-dropdown-text">
                        {labelPart && <><span className="filter-label">{labelPart}:</span>{' '}</>}
                        <span className="filter-value">{valuePart}</span>
                    </span>
                    {!isDefaultValue ? (
                        <span className="filter-dropdown-icon-wrapper" onClick={handleClearAll}>
                            <Icon name="close-search" className="filter-dropdown-icon" />
                        </span>
                    ) : (
                        <Icon name="feather-chevron-down" className="filter-dropdown-icon" />
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu className={`filter-dropdown-menu multi-select-menu ${positionClass}`}>
                    <MultiSelectFilterList
                        key={openKey}
                        values={draft}
                        options={options}
                        onChange={setDraft}
                        searchPlaceholder={searchPlaceholder}
                        emptyLabel={emptyLabel}
                    />

                    <div className="multi-select-footer">
                        <button type="button" className="multi-select-clear" onClick={handleClearDraft}>
                            {CLEAR_LABEL}
                        </button>
                        <button type="button" className="multi-select-apply" onClick={handleApply}>
                            {`${APPLY_LABEL} (${draft.length})`}
                        </button>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default MultiSelectFilterDropdown;
